/**
 * Module Field Definitions
 * Maps each module to its available fields based on backend models
 * This helps prevent typos and ensures consistency when adding fields to permission system
 */

export const MODULE_FIELDS = {
  clients: [
    'client_number',
    'client_name',
    'client_type',
    'industry',
    'business_relation_start_date',
    'phone',
    'fax',
    'website',
    'time_zone',
    'address_line_1',
    'address_line_2',
    'city',
    'zip_code',
    'state',
    'country',
    'approval_billing_percentage',
    'fabrication_billing_percentage',
    'special_notes'
  ],
  
  contacts: [
    'client_id',
    'first_name',
    'last_name',
    'business_phone',
    'business_extension',
    'mobile_phone',
    'email',
    'designation',
    'roles',
    'special_notes'
  ],
  
  estimations: [
    'estimation_number',
    'client_id',
    'project_name',
    'sales',
    'proposal_link',
    'remarks',
    'line_items',
    'estimation_received_date',
    'due_date',
    'submitted_date',
    'awarded_date',
    'detailing_cost',
    'engineering_cost',
    'total_project_value',
    'schedule_weeks',
    'schedule_days',
    'submission_status',
    'award_status',
    'version',
    'parent_estimation_id',
    'show_itemwise_schedule'
  ],
  
  projects: [
    'project_number',
    'name',
    'estimation_id',
    'client_id',
    'client_name',
    'manager',
    'status',
    'description',
    'awarded_date',
    'start_date',
    'end_date',
    'base_price',
    'detailing_cost',
    'engineering_cost',
    'estimated_hours',
    'allocated_hours',
    'allocated_schedule_weeks',
    'allocated_schedule_days',
    'branches',
    'branch_allocations',
    'change_orders',
    'scopes'
  ],
  
  invoices: [
    'invoice_number',
    'project_id',
    'project_reference_invoice_number',
    'client_id',
    'billing_contact_id',
    'issue_date',
    'due_date',
    'amount',
    'payment_entries',
    'amount_paid',
    'payment_status',
    'notes'
  ],
  
  timesheets: [
    'timesheet_id',
    'user_id',
    'project_id',
    'task_description',
    'date',
    'hours',
    'status',
    'approved_by',
    'approved_date',
    'rejection_reason',
    'department'
  ],
  
  users: [
    'user_id',
    'username',
    'email',
    'full_name',
    'department',
    'access_level',
    'employee_id',
    'client_id',
    'password_hash'
  ],
  
  employees: [
    'employee_id',
    'employee_number',
    'first_name',
    'last_name',
    'full_name',
    'email',
    'phone',
    'designation',
    'department',
    'hire_date',
    'employee_status',
    'reporting_manager',
    'branch',
    'address',
    'city',
    'state',
    'zip_code',
    'country',
    'emergency_contact_name',
    'emergency_contact_phone',
    'salary',
    'hourly_rate'
  ],
  
  teams: [
    'team_id',
    'team_name',
    'team_lead',
    'members',
    'department',
    'description'
  ],
  
  vendors: [
    'vendor_id',
    'vendor_number',
    'vendor_name',
    'vendor_type',
    'industry',
    'business_relation_start_date',
    'phone',
    'fax',
    'website',
    'time_zone',
    'address_line_1',
    'address_line_2',
    'city',
    'zip_code',
    'state',
    'country'
  ],
  
  vendor_contacts: [
    'vendor_id',
    'first_name',
    'last_name',
    'business_phone',
    'business_extension',
    'mobile_phone',
    'email',
    'designation',
    'roles',
    'special_notes'
  ],
  
  tasks: [
    'task_id',
    'project_id',
    'task_name',
    'description',
    'assigned_to',
    'status',
    'priority',
    'due_date',
    'created_by',
    'created_at'
  ],
  
  change_orders: [
    'change_order_id',
    'change_order_number',
    'project_id',
    'description',
    'status',
    'cost_impact',
    'time_impact',
    'created_at',
    'updated_at'
  ],
  
  submittals: [
    'submittal_id',
    'project_id',
    'submittal_name',
    'scope',
    'status',
    'weightage',
    'completion_percentage',
    'hold_enabled',
    'hold_reason',
    'steps'
  ],
  
  payment_reminders: [
    'reminder_id',
    'invoice_id',
    'reminder_number',
    'reminder_date',
    'notes',
    'sent_by'
  ]
};

/**
 * Get available fields for a module (excluding already configured fields)
 * @param {string} module - Module name
 * @param {Object} existingFields - Already configured fields for this module
 * @returns {Array} List of available field names
 */
export function getAvailableFields(module, existingFields = {}) {
  const allFields = MODULE_FIELDS[module] || [];
  const configuredFields = Object.keys(existingFields);
  
  return allFields.filter(field => !configuredFields.includes(field));
}

/**
 * Get all modules that have field definitions
 * @returns {Array} List of module names
 */
export function getAllModules() {
  return Object.keys(MODULE_FIELDS);
}
